/**
 * FormSelect Component
 * 
 * Standardized select component for forms using MUI Select.
 * 
 * @example
 * <FormSelect
 *   name="status"
 *   label="Status"
 *   value={status}
 *   onChange={(e) => setStatus(e.target.value)}
 *   options={[
 *     { value: 'active', label: 'Active' },
 *     { value: 'inactive', label: 'Inactive' },
 *   ]}
 * />
 */

import React from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  SelectProps,
  type SxProps,
  type Theme,
} from '@mui/material';

export interface SelectOption {
  value: string | number;
  label: string;
  disabled?: boolean;
  sx?: SxProps<Theme>;
}

export interface FormSelectProps extends Omit<SelectProps, 'variant'> {
  /** Field name */
  name: string;
  /** Field label */
  label: string;
  /** Select options */
  options: SelectOption[];
  /** Error message */
  errorMessage?: string;
  /** Helper text */
  helperText?: string;
  /** Variant style */
  variant?: 'outlined' | 'filled' | 'standard';
  /** Placeholder text */
  placeholder?: string;
  /** Render the dropdown menu inline instead of portaling to document.body (use inside custom modals) */
  disablePortal?: boolean;
  /** Label sits above the field instead of inside the outline. */
  stackedLabel?: boolean;
}

export const FormSelect: React.FC<FormSelectProps> = ({
  name,
  label,
  options,
  errorMessage,
  helperText,
  variant = 'outlined',
  placeholder,
  disablePortal = false,
  stackedLabel = false,
  MenuProps,
  ...props
}) => {
  const hasError = Boolean(errorMessage);
  const labelId = `${name}-label`;

  const hasPlaceholder = Boolean(placeholder);

  const mergedMenuProps = {
    disableScrollLock: true,
    disablePortal,
    ...MenuProps,
    PaperProps: {
      ...MenuProps?.PaperProps,
      sx: {
        zIndex: 10000,
        ...MenuProps?.PaperProps?.sx,
      },
    },
  };

  const select = (
    <FormControl
      fullWidth
      error={hasError}
      variant={variant}
      required={stackedLabel ? false : props.required}
      data-field={name}
    >
      {!stackedLabel && (
      <InputLabel id={labelId} shrink={hasPlaceholder || undefined}>
        {label}
      </InputLabel>
      )}
      <Select
        name={name}
        labelId={labelId}
        displayEmpty={hasPlaceholder}
        notched={stackedLabel ? false : hasPlaceholder || undefined}
        renderValue={
          hasPlaceholder
            ? (selected) => {
                if (selected === '' || selected === undefined || selected === null) {
                  return <span style={{ color: '#8A867F' }}>{placeholder}</span>;
                }
                const match = options.find(
                  (option) => String(option.value) === String(selected),
                );
                return match?.label ?? '';
              }
            : undefined
        }
        sx={{
          borderRadius: stackedLabel ? '8px' : 2,
          ...(stackedLabel
            ? {
                height: 44,
                fontSize: 16,
                backgroundColor: '#fff',
                '& .MuiOutlinedInput-notchedOutline': { borderColor: '#D1D5DB' },
                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#9CA3AF' },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#1C1B1A' },
              }
            : {}),
        }}
        label={stackedLabel ? undefined : label}
        MenuProps={mergedMenuProps}
        {...props}
      >
        {placeholder && (
          <MenuItem value="" disabled>
            <em>{placeholder}</em>
          </MenuItem>
        )}
        {options.map((option) => (
          <MenuItem
            key={option.value}
            value={option.value}
            disabled={option.disabled}
            sx={option.sx}
          >
            {option.label}
          </MenuItem>
        ))}
      </Select>
      {(hasError || helperText) && (
        <FormHelperText>{hasError ? errorMessage : helperText}</FormHelperText>
      )}
    </FormControl>
  );

  if (!stackedLabel) return select;

  return (
    <Box>
      <Box
        component="label"
        id={labelId}
        sx={{ display: 'block', mb: '6px', fontSize: 14, fontWeight: 600, color: '#1C1B1A' }}
      >
        {label}
        {props.required && (
          <Box component="span" sx={{ color: '#A8213A' }}> *</Box>
        )}
      </Box>
      {select}
    </Box>
  );
};

export default FormSelect;
